import { useState, useMemo } from "react";
import { C, MUSCLE_GROUPS, EXERCISE_LIBRARY } from "../constants";
import { Btn, Badge } from "./ui";
import { CustomExerciseModal } from "./CustomExerciseModal";

const typeBadgeColor = (type) =>
  type === "barbell" ? "orange" : type === "dumbbell" ? "blue" : "accent";

export const ExerciseLibraryTab = ({ customExercises, userExerciseOverrides = {}, programs = [], activeProgramId, onAddCustomExercise, onUpdateCustomExercise, onDeleteCustomExercise, onSetExerciseOverride, onResetExerciseOverride, advancedMode = false }) => {
  const [query,    setQuery]    = useState("");
  const [scope,    setScope]    = useState("all"); // "all" | "active"
  const [editing,  setEditing]  = useState(undefined);

  const activeProgram = useMemo(
    () => programs.find(p => p.id === activeProgramId) || null,
    [programs, activeProgramId]
  );

  const activePlanNames = useMemo(() => {
    if (!activeProgram) return null;
    const names = new Set();
    activeProgram.plans.forEach(plan =>
      plan.exercises.forEach(ex => names.add(ex.name.toLowerCase()))
    );
    return names;
  }, [activeProgram]);

  const allExercises = useMemo(() => [
    ...EXERCISE_LIBRARY.map(e => {
      const ov = userExerciseOverrides[e.id] || userExerciseOverrides[e.name.toLowerCase()];
      return ov ? { ...e, ...ov, isCustom: false, hasOverride: true } : { ...e, isCustom: false, hasOverride: false };
    }),
    ...customExercises.map(e => ({ ...e, isCustom: true })),
  ], [customExercises, userExerciseOverrides]);

  const filtered = useMemo(() => {
    let list = allExercises;
    if (scope === "active" && activePlanNames) {
      list = list.filter(e => activePlanNames.has(e.name.toLowerCase()));
    }
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(e => e.name.toLowerCase().includes(q));
    }
    return list;
  }, [allExercises, query, scope, activePlanNames]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(ex => {
      if (!g[ex.muscleGroup]) g[ex.muscleGroup] = [];
      g[ex.muscleGroup].push(ex);
    });
    return g;
  }, [filtered]);

  const handleSave = (libEntry) => {
    if (editing && !editing.isCustom && editing.name) {
      // Editing a default exercise — only persist increment overrides
      onSetExerciseOverride(editing.id || null, editing.name, {
        useCustomIncrement: !!libEntry.useCustomIncrement,
        increment: libEntry.useCustomIncrement ? libEntry.increment : null,
      });
    } else if (editing && editing.isCustom) {
      onUpdateCustomExercise(editing, libEntry);
    } else {
      onAddCustomExercise(libEntry);
    }
  };

  const handleSecondary = (ex) => {
    if (ex.isCustom) onDeleteCustomExercise(ex);
    else if (ex.hasOverride) onResetExerciseOverride(ex.id || null, ex.name);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 18 }}>
        <div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 800, color: C.text, lineHeight: 1 }}>
            Exercise Library
          </div>
          <div style={{ color: C.muted, fontSize: 12, marginTop: 6 }}>
            {EXERCISE_LIBRARY.length} default · {customExercises.length} custom
          </div>
        </div>
        <Btn variant="primary" onClick={() => setEditing(null)}>+ Add Exercise</Btn>
      </div>

      <input
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Search exercises..."
        style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, padding: "12px 16px", fontSize: 15, width: "100%", outline: "none", marginBottom: 12 }}
      />

      <div style={{ display: "flex", gap: 6, marginBottom: 22, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 4 }}>
        {[
          { id: "all",    label: "All Exercises" },
          { id: "active", label: activeProgram ? `In ${activeProgram.name}` : "In Active Plan", disabled: !activeProgram },
        ].map(opt => (
          <button key={opt.id}
            onClick={() => !opt.disabled && setScope(opt.id)}
            disabled={opt.disabled}
            style={{
              flex: 1, padding: "8px 12px", borderRadius: 7, border: "none",
              cursor: opt.disabled ? "not-allowed" : "pointer",
              background: scope === opt.id ? C.accent : "transparent",
              color: scope === opt.id ? "#1e1d1b" : opt.disabled ? C.border : C.muted,
              fontWeight: 600, fontSize: 12, fontFamily: "inherit",
              opacity: opt.disabled ? 0.5 : 1,
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {MUSCLE_GROUPS.map(group => {
        const items = grouped[group];
        if (!items || items.length === 0) return null;
        return (
          <div key={group} style={{ marginBottom: 22 }}>
            <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
              {group} <span style={{ color: C.textDim, fontWeight: 400 }}>({items.length})</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {items.map(ex => (
                <div key={`${ex.isCustom ? "c" : "d"}:${ex.name}`}
                  onClick={() => setEditing(ex)}
                  style={{
                    background: C.card,
                    border: `1px solid ${ex.isCustom ? C.blue + "55" : ex.hasOverride ? C.orange + "55" : C.border}`,
                    borderRadius: 9, padding: "11px 14px", cursor: "pointer",
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {ex.name}
                    </span>
                    {ex.isCustom && (
                      <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: C.blue + "33", color: C.blue, textTransform: "uppercase", letterSpacing: "0.06em" }}>Custom</span>
                    )}
                    {!ex.isCustom && ex.hasOverride && (
                      <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: C.orange + "33", color: C.orange, textTransform: "uppercase", letterSpacing: "0.06em" }}>Edited</span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                    {ex.p && <Badge>{ex.p}</Badge>}
                    <Badge color={typeBadgeColor(ex.type)}>{ex.type || "machine"}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {filtered.length === 0 && (
        <div style={{ color: C.muted, fontSize: 14, textAlign: "center", padding: "40px 0" }}>
          No exercises match "{query}"
        </div>
      )}

      {editing !== undefined && (
        <CustomExerciseModal
          initial={editing}
          lockMetadata={editing && !editing.isCustom}
          onSave={handleSave}
          onClose={() => setEditing(undefined)}
          onDelete={editing && (editing.isCustom || editing.hasOverride) ? handleSecondary : null}
          deleteLabel={editing?.isCustom ? "Delete" : "Reset"}
          advancedMode={advancedMode}
        />
      )}
    </div>
  );
};
